import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/api/client';

// API response wrapper type
interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

// API Inventory Process response type
interface ApiInventoryProcess {
  inventoryProcessId: number;
  inventoryDate: string;
  locationId: number;
  locationName: string | null;
  clientId: number | null;
  clientName: string | null;
  userName: string | null;
  closed: boolean;
  closedDate: string | null;
  totalAssets: number;
  scannedAssets: number;
  incidentsCount: number;
}

// interface ApiInventoryProcessDetail extends ApiInventoryProcess {
//   assets: ApiInventoryAsset[];
// }

interface ApiInventoryAsset {
  assetId: string;
  assetDescription: string | null;
  epc: string;
  locationId: number;
  locationName: string | null;
  scanDate: string | null;
  assetStatus: number;
  status: string;
  hasIncidents: boolean;
  incidentsCount: number;
}

export interface InventoryProcess {
  id: number;
  name: string;
  locationId: number | null;
  locationName: string | null;
  clientId: number | null;
  clientName: string | null;
  status: 'Open' | 'InProgress' | 'Closed';
  startDate: string;
  endDate: string | null;
  totalAssets: number;
  scannedAssets: number;
  missingAssets: number;
  createdBy: string;
  createdAt: string;
}

export interface InventoryAsset {
  id: string;
  assetId: string;
  assetCode: string;
  assetName: string;
  epcCode: string | null;
  scanned: boolean;
  scannedAt: string | null;
  status: string;
  statusCode: number;
  hasIncidents: boolean;
}

export interface InventorySearchParams {
  status?: string;
  locationId?: number;
  clientId?: number;
  page?: number;
  pageSize?: number;
}

// Query keys
export const inventoryKeys = {
  all: ['inventory'] as const,
  lists: () => [...inventoryKeys.all, 'list'] as const,
  list: (params: InventorySearchParams) => [...inventoryKeys.lists(), params] as const,
  details: () => [...inventoryKeys.all, 'detail'] as const,
  detail: (id: number) => [...inventoryKeys.details(), id] as const,
  assets: (id: number) => [...inventoryKeys.detail(id), 'assets'] as const,
  stats: (id: number) => [...inventoryKeys.detail(id), 'stats'] as const,
  incidents: (id: number) => [...inventoryKeys.detail(id), 'incidents'] as const,
  adjustments: (id: number) => [...inventoryKeys.detail(id), 'adjustments'] as const,
  unexpectedEpcs: (id: number, page: number) =>
    [...inventoryKeys.detail(id), 'unexpected-epcs', page] as const,
  active: (locationId: number) => [...inventoryKeys.all, 'active', locationId] as const,
};

// Phase 15 — closed-vocab adjustment actions accepted by the backend.
export type InventoryAdjustmentAction = 'ACCEPT_LOCATION' | 'MARK_MISSING';

export interface InventoryAdjustment {
  inventoryAdjustmentId: number;
  assetId: string;
  action: InventoryAdjustmentAction;
  previousLocationId: number | null;
  newLocationId: number | null;
  userId: number;
  userName: string | null;
  adjustmentDate: string;
  notes: string | null;
}

// Transform API Inventory Process to our interface
function transformProcess(apiProc: ApiInventoryProcess): InventoryProcess {
  return {
    id: apiProc.inventoryProcessId,
    name: `Inventario ${apiProc.inventoryProcessId}`,
    locationId: apiProc.locationId,
    locationName: apiProc.locationName,
    clientId: apiProc.clientId ?? null,
    clientName: apiProc.clientName ?? null,
    status: apiProc.closed ? 'Closed' : (apiProc.scannedAssets > 0 ? 'InProgress' : 'Open'),
    startDate: apiProc.inventoryDate,
    endDate: apiProc.closedDate,
    totalAssets: apiProc.totalAssets,
    scannedAssets: apiProc.scannedAssets,
    missingAssets: apiProc.totalAssets - apiProc.scannedAssets,
    createdBy: apiProc.userName || 'Unknown',
    createdAt: apiProc.inventoryDate,
  };
}

// Transform API Inventory Asset to our interface
function transformAsset(apiAsset: ApiInventoryAsset): InventoryAsset {
  return {
    id: apiAsset.assetId,
    assetId: apiAsset.assetId,
    assetCode: apiAsset.assetId,
    assetName: apiAsset.assetDescription || apiAsset.assetId,
    epcCode: apiAsset.epc || null,
    scanned: apiAsset.assetStatus !== 0,
    scannedAt: apiAsset.scanDate,
    status: apiAsset.status,
    statusCode: apiAsset.assetStatus,
    hasIncidents: apiAsset.hasIncidents,
  };
}

// Get inventory processes
export function useInventoryProcesses(params: InventorySearchParams = {}) {
  return useQuery({
    queryKey: inventoryKeys.list(params),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiInventoryProcess[]>>('/api/inventory/list');
      let processes = response.data.data.map(transformProcess);

      // Client-side filtering
      if (params.status) {
        processes = processes.filter(p => p.status === params.status);
      }
      if (params.locationId) {
        processes = processes.filter(p => p.locationId === params.locationId);
      }
      if (params.clientId) {
        processes = processes.filter(p => p.clientId === params.clientId);
      }

      // Apply pagination
      const page = params.page || 1;
      const pageSize = params.pageSize || 25;
      const startIndex = (page - 1) * pageSize;
      const paginatedItems = processes.slice(startIndex, startIndex + pageSize);

      return {
        items: paginatedItems,
        totalCount: processes.length,
        totalPages: Math.ceil(processes.length / pageSize),
        page,
        pageSize,
      };
    },
  });
}

// Get single inventory process
export function useInventoryProcess(id: number, enabled = true) {
  return useQuery({
    queryKey: inventoryKeys.detail(id),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiInventoryProcess>>(`/api/inventory/${id}`);
      return transformProcess(response.data.data);
    },
    enabled: enabled && id > 0,
  });
}

// Get inventory assets (unpaginated - for backward compatibility)
export function useInventoryAssets(id: number, enabled = true) {
  return useQuery({
    queryKey: inventoryKeys.assets(id),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiInventoryAsset[]>>(`/api/inventory/${id}/assets`);
      return response.data.data.map(transformAsset);
    },
    enabled: enabled && id > 0,
  });
}

// Search params for paginated inventory assets
export interface InventoryAssetsSearchParams {
  page?: number;
  pageSize?: number;
  statusFilter?: number[];
}

// Paginated API response type
interface PaginatedInventoryAssetsResponse {
  items: ApiInventoryAsset[];
  totalRecords: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Get inventory assets with server-side pagination and filtering
export function useInventoryAssetsPaginated(
  id: number,
  params: InventoryAssetsSearchParams,
  enabled = true
) {
  return useQuery({
    queryKey: [...inventoryKeys.assets(id), 'paginated', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.append('page', String(params.page || 1));
      searchParams.append('pageSize', String(params.pageSize || 25));
      if (params.statusFilter?.length) {
        params.statusFilter.forEach(s => searchParams.append('statusFilter', String(s)));
      }

      const response = await api.get<ApiResponse<PaginatedInventoryAssetsResponse>>(
        `/api/inventory/${id}/assets?${searchParams.toString()}`
      );
      const data = response.data.data;
      return {
        items: data.items.map(transformAsset),
        totalCount: data.totalRecords,
        totalPages: data.totalPages,
        page: data.page,
        pageSize: data.pageSize,
      };
    },
    enabled: enabled && id > 0,
    placeholderData: keepPreviousData, // Smooth page transitions
  });
}

// Get inventory stats
export function useInventoryStats(id: number, enabled = true) {
  return useQuery({
    queryKey: inventoryKeys.stats(id),
    queryFn: async () => {
      const response = await api.get<ApiResponse<{ key: number; value: number }[]>>(`/api/inventory/${id}/stats`);
      const stats = response.data.data;
      return {
        pending: (stats.find(s => s.key === 0)?.value || 0) + (stats.find(s => s.key === 4)?.value || 0),
        ok: stats.find(s => s.key === 1)?.value || 0,
        wrongLocation: stats.find(s => s.key === 2)?.value || 0,
        notFound: stats.find(s => s.key === 3)?.value || 0,
      };
    },
    enabled: enabled && id > 0,
  });
}

/**
 * EPC leído durante el inventario que no formaba parte del esperado.
 *
 * `unregistered` = el EPC no existe en el sistema: tarima física sin dar de
 * alta. `registered` = sí existe, pero su tarima no se esperaba aquí (suele
 * estar marcada como Entregada o En tránsito), así que lo que está mal es el
 * estatus, no el alta.
 */
export interface UnexpectedEpc {
  epc: string;
  kind: 'unregistered' | 'registered';
  assetId: string | null;
  assetStatus: number;
  assetStatusName: string | null;
  locationName: string | null;
}

export interface UnexpectedEpcsPage {
  items: UnexpectedEpc[];
  unregisteredCount: number;
  registeredCount: number;
  totalRecords: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Get EPCs the inventory read but did not expect
export function useInventoryUnexpectedEpcs(id: number, page = 1, enabled = true) {
  return useQuery({
    queryKey: inventoryKeys.unexpectedEpcs(id, page),
    queryFn: async () => {
      const response = await api.get<ApiResponse<UnexpectedEpcsPage>>(
        `/api/inventory/${id}/unexpected-epcs`,
        { params: { page, pageSize: 25 } }
      );
      return response.data.data;
    },
    enabled: enabled && id > 0,
    placeholderData: keepPreviousData,
  });
}

// Get inventory incidents
export function useInventoryIncidents(id: number, enabled = true) {
  return useQuery({
    queryKey: inventoryKeys.incidents(id),
    queryFn: async () => {
      const response = await api.get<ApiResponse<unknown[]>>(`/api/inventory/${id}/incidents`);
      return response.data.data;
    },
    enabled: enabled && id > 0,
  });
}

// Get active inventory for location
export function useActiveInventory(locationId: number, enabled = true) {
  return useQuery({
    queryKey: inventoryKeys.active(locationId),
    queryFn: async () => {
      const response = await api.get<ApiResponse<{ inventoryProcessId: number }>>('/api/mobile/inventory/process/active', {
        params: { locationId },
      });
      return response.data.data.inventoryProcessId > 0 ? response.data.data.inventoryProcessId : null;
    },
    enabled: enabled && locationId > 0,
  });
}

// Create inventory process from web UI
export function useCreateInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { locationId: number }) => {
      const response = await api.post<ApiResponse<{ inventoryProcessId: number; totalAssets: number }>>('/api/inventory/create', {
        locationId: data.locationId,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.active(variables.locationId) });
    },
  });
}

// Start inventory process from mobile
export function useStartInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { locationId: number }) => {
      const response = await api.get<ApiResponse<{ inventoryProcessId: number; totalAssets: number }>>('/api/mobile/inventory/process/start', {
        params: { locationId: data.locationId },
      });
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.active(variables.locationId) });
    },
  });
}

// Scan EPC in inventory
export function useScanInventoryEpc() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, epcCode }: { id: number; epcCode: string }) => {
      const response = await api.post<ApiResponse<{
        assetId: string;
        assetDescription: string;
        status: number;
        locationMatch: boolean;
      }>>('/api/mobile/inventory/process/scan', {
        inventoryProcessId: id,
        epcTag: epcCode,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.assets(variables.id) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.stats(variables.id) });
    },
  });
}

// Close inventory process
export function useCloseInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.get<ApiResponse<unknown>>('/api/mobile/inventory/process/finalize', {
        params: { inventoryProcessId: id },
      });
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.lists() });
    },
  });
}

// Phase 15 — list adjustments already applied to a process.
export function useInventoryAdjustments(id: number, enabled = true) {
  return useQuery({
    queryKey: inventoryKeys.adjustments(id),
    queryFn: async () => {
      const response = await api.get<ApiResponse<InventoryAdjustment[]>>(
        `/api/inventory/${id}/adjustments`
      );
      return response.data.data;
    },
    enabled: enabled && id > 0,
  });
}

// Phase 15 — apply a reconciliation decision to one row of a closed
// inventory process. The mutation invalidates assets/stats/adjustments
// so the Reconciliar panel and the underlying table refresh in lockstep.
export function useAdjustInventoryAsset(processId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      assetId: string;
      action: InventoryAdjustmentAction;
      notes?: string;
    }) => {
      const response = await api.post<ApiResponse<{
        inventoryAdjustmentId: number;
        assetId: string;
        action: InventoryAdjustmentAction;
        previousLocationId: number | null;
        newLocationId: number | null;
        adjustmentDate: string;
        newAssetStatus: number;
      }>>(`/api/inventory/${processId}/adjust`, input);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.detail(processId) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.assets(processId) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.stats(processId) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.adjustments(processId) });
    },
  });
}

/**
 * Baja de un proceso de inventario (soft delete en el servidor: apaga IsActive).
 *
 * Sólo procede sobre un inventario abierto y sin lecturas; el servidor rechaza
 * lo demás. Antes esto era un stub que sólo escribía en consola, así que el
 * diálogo se cerraba como si hubiera borrado y la fila seguía en la lista.
 */
export function useDeleteInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete<ApiResponse<null>>(`/api/inventory/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.lists() });
    },
  });
}
